import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, createVNode as _createVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, resolveDirective as _resolveDirective, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = ["id"]
const _hoisted_2 = { class: "query-builder-group-options-logic" }
const _hoisted_3 = { class: "query-builder-group-options-actions" }
const _hoisted_4 = { class: "query-builder-rule-container" }
const _hoisted_5 = ["id"]
const _hoisted_6 = { class: "text-black-50 mx-2" }
const _hoisted_7 = {
  key: 0,
  class: "d-inline-block"
}
const _hoisted_8 = {
  key: 0,
  class: "query-builder-lower-group-container"
}
const _hoisted_9 = ["id"]

import MSelect from "@/BasicComponentLibrary/MSelect.vue";
import Alert from "@common/components/Alert.vue";
import Icon from "@common/components/Icon.vue";
import MenuBar from "@common/components/MenuBar/MenuBar.vue";
import {
  FetchableData,
  FetchableDataEntry,
  QueryBuilderChildGroup,
  QueryBuilderData,
  QueryBuilderGroup,
  QueryBuilderRootGroup
} from "@medapro-types/AdvancedSearch/components/QueryBuilder";
import { RootState } from "@medapro-types/common/rootstate";
import { Guid } from "@util/guid";
import _ from "lodash";
import { Ref, computed, h, inject, nextTick, onMounted, reactive, ref, unref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useState } from "vuex-composition-helpers";
import Rule from "./Rule.vue";

//#region General
type ViewModel = {
  id: string;
  localModel: QueryBuilderGroup;
  selectedColumnForRule: FetchableDataEntry | undefined;
  showAttributeGroupDialog: boolean;
  showAttributeDialog: boolean;
  isNewlyCreated: boolean;
};


export default /*#__PURE__*/_defineComponent({
  __name: 'Group',
  props: {
    value: {},
    depth: { default: 0 },
    path: {}
  },
  emits: ["input", "deleteGroup", "refreshBackground"],
  setup(__props: any, { emit: emits }) {

const props = __props;

const { t: $t } = useI18n();
const { meta } = useState<RootState>(["meta"]);




//#endregion

//#region refs
const selfGroup = ref<Element>();
const queryBuilderGroupOptions = ref<typeof MenuBar>();
const queryBuilderRuleCriteriaControl = ref<Element>();
const groupWrapper = ref<Element[]>([]);
const ruleWrapper = ref<Element[]>([]);
//#endregion

//#region Path

const getCurrentPath = () => {
  return `${props.path}|${vm.id}`;
};

//#endregion

//#region Fetched Data
const fetchedData = inject<Ref<FetchableData>>("fetchedData");

const hasGroupRule = computed<boolean>(() => {
  return vm.localModel.rules.length;
});

const possibleSelection = computed(() => {
  const unrefFetchedData = unref(fetchedData);
  if (!unrefFetchedData) return [];
  for (const item of unrefFetchedData) {
    item["localizedKey"] = $t("AdvancedSearch.SearchType." + item.searchType);
  }
  return _.sortBy(unrefFetchedData, (x) => x.localizedKey);
});
//#endregion

//#region TypeGuard
const isQueryBuilderGroup = (e: unknown): e is QueryBuilderGroup => {
  return !!e && Object.hasOwn(e, "groupId") && Object.hasOwn(e, "logic") && Object.hasOwn(e, "rules") && Object.hasOwn(e, "groups");
};
//#endregion

//#region ViewModel
const idHelper = Guid.new();

let initialModel: QueryBuilderGroup;

if (isQueryBuilderGroup(props.value)) {
  initialModel = props.value;
} else if (props.depth === 0) {
  initialModel = {
    groupId: idHelper,
    logic: "AND",
    isCaseSensitive: false,
    groups: [],
    rules: []
  } satisfies QueryBuilderRootGroup;
} else {
  initialModel = {
    groupId: idHelper,
    logic: "AND",
    groups: [],
    rules: []
  } satisfies QueryBuilderChildGroup;
}

const vm = reactive<ViewModel>({
  id: isQueryBuilderGroup(props.value) ? (props.value.groupId ?? idHelper) : idHelper,
  localModel: initialModel,
  selectedColumnForRule: undefined,
  showAttributeGroupDialog: false,
  showAttributeDialog: false,
  isNewlyCreated: true
});

//#endregion

//#region Rules
const rules = computed(() => {
  return vm.localModel.rules;
});

const addRule = () => {
  if (!vm.selectedColumnForRule) return;

  vm.localModel.rules.push({
    ruleId: Guid.new(),
    operator: null,
    searchType: vm.selectedColumnForRule.searchType,
    value: vm.selectedColumnForRule.uiType == "date" ? "unselected" : ""
  });
  vm.isNewlyCreated = false;
  triggerRefresh();
};

const onDeleteRule = (event: { id: string }) => {
  const index = vm.localModel.rules.findIndex((x) => x.ruleId == event.id);
  vm.localModel.rules.splice(index, 1);
  triggerRefresh();
};
//#endregion

//#region Groups
const groups = computed(() => {
  return vm.localModel.groups;
});

const addGroup = () => {
  vm.localModel.groups.push(getDefaultGroup());
  nextTick(() => {
    _.last(unref(groupWrapper))?.scrollIntoView();
  });
  triggerRefresh();
};
const getDefaultGroup = (): QueryBuilderChildGroup => {
  return { groupId: Guid.new(), logic: "AND", groups: [], rules: [] };
};

const onDeleteGroup = (group: QueryBuilderChildGroup) => {
  const index = vm.localModel.groups.indexOf(group);
  vm.localModel.groups.splice(index, 1);
  triggerRefresh();
};
//#endregion

//#region Update vModel
watch(
  () => props.value,
  () => {
    if (isQueryBuilderGroup(props.value)) {
      vm.localModel = props.value;
    } else if (props.depth === 0) {
      vm.localModel = {
        groupId: Guid.new(),
        logic: "AND",
        isCaseSensitive: false,
        groups: [],
        rules: []
      } satisfies QueryBuilderRootGroup;
    } else {
      vm.localModel = {
        groupId: Guid.new(),
        logic: "AND",
        groups: [],
        rules: []
      } satisfies QueryBuilderChildGroup;
    }
    vm.isNewlyCreated = !hasGroupRule.value;
    triggerRefresh(50);
  }
);
watch(
  () => vm.localModel,
  (nv) => {
    emits("input", nv);
  },
  { deep: true, immediate: true }
);
//#endregion

//#region Background

const lineCoordinates = ref<{ x0: number; y0: number; x1: number; y1: number }[]>([]);

const refreshLineCoordiantes = () => {
  lineCoordinates.value = [];
  const optionRect = unref(queryBuilderGroupOptions)?.$el.getBoundingClientRect();

  const selfGroupRect = unref(selfGroup)?.getBoundingClientRect();
  const queryBuilderRuleCriteriaControlRect = unref(queryBuilderRuleCriteriaControl)?.getBoundingClientRect();
  const offsetX = -(selfGroupRect?.x ?? 0),
    offsetY = -(selfGroupRect?.y ?? 0);

  const lastGroupRect = _.last(unref(groupWrapper))?.getBoundingClientRect();
  const lowestElementPos: number =
    _.max([
      (lastGroupRect?.y ?? 0) + offsetY + (optionRect?.height ?? 0) / 2,
      (queryBuilderRuleCriteriaControlRect?.y ?? 0) + offsetY + (queryBuilderRuleCriteriaControlRect?.height ?? 0) / 2
    ]) ?? 0;

  const x0 = (optionRect?.x ?? 0) + offsetX + 10;
  const y0 = (optionRect?.y ?? 0) + (optionRect?.height ?? 0) / 2 + offsetY;
  const x1 = x0;
  const y1 = lowestElementPos;

  lineCoordinates.value.push({ x0, y0, x1, y1 });
  const ruleRects = unref(ruleWrapper).map((x) => x.getBoundingClientRect());
  if (queryBuilderRuleCriteriaControlRect) ruleRects.push(queryBuilderRuleCriteriaControlRect);
  for (const ruleRect of ruleRects) {
    lineCoordinates.value.push({
      x0: x1,
      y0: ruleRect.y + offsetY + ruleRect.height / 2,
      x1: ruleRect.x + offsetX + 0,
      y1: ruleRect.y + offsetY + ruleRect.height / 2
    });
  }

  for (const group of unref(groupWrapper)) {
    const groupRect = group.getBoundingClientRect();
    lineCoordinates.value.push({
      x0: x1,
      y0: groupRect.y + offsetY + optionRect.height / 2,
      x1: groupRect.x + offsetX /* + 20 */,
      y1: groupRect.y + offsetY + optionRect.height / 2
    });
  }

  nextTick(() => {
    emits("refreshBackground");
  });
};

const background = () => {
  return h("svg", { class: "background-lines-svg", style: `z-index:${-100 + props.depth};` }, [
    lineCoordinates.value.map((coort) => {
      return h("line", {
        class: "background-lines-svg-line",
        x1: coort.x0,
        y1: coort.y0,
        x2: coort.x1,
        y2: coort.y1
      });
    })
  ]);
};

const triggerRefresh = (triggerAfterMS: number = 0) => {
  setTimeout(() => {
    nextTick(() => {
      refreshLineCoordiantes();
    });
  }, triggerAfterMS);
};
//#endregion

//#region LifeCycle
onMounted(() => {
  if (!vm.localModel.groupId) {
    vm.localModel.groupId = vm.id;
  }
  vm.isNewlyCreated = !hasGroupRule.value;
  triggerRefresh(50);
});
//#endregion

return (_ctx: any,_cache: any) => {
  const _component_b_btn = _resolveComponent("b-btn")!
  const _component_b_form_checkbox = _resolveComponent("b-form-checkbox")!
  const _component_group = _resolveComponent("group", true)!
  const _directive_tooltip = _resolveDirective("tooltip")!

  return (_openBlock(), _createElementBlock("div", {
    class: "query-builder-group",
    id: vm.id,
    ref_key: "selfGroup",
    ref: selfGroup
  }, [
    _createVNode(MenuBar, {
      class: "query-builder-group-options",
      ref_key: "queryBuilderGroupOptions",
      ref: queryBuilderGroupOptions,
      "without-border": ""
    }, {
      leftItems: _withCtx(() => [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", {
            class: "query-builder-group-options-logic-selection",
            onClick: _cache[0] || (_cache[0] = ($event: any) => (vm.localModel.logic = vm.localModel.logic == 'AND' ? 'OR' : 'AND'))
          }, [
            _createElementVNode("div", {
              class: _normalizeClass(["and-selection", { active: vm.localModel.logic == 'AND' }])
            }, _toDisplayString(_unref($t)("And")), 3),
            _createElementVNode("div", {
              class: _normalizeClass(["or-selection", { active: vm.localModel.logic == 'OR' }])
            }, _toDisplayString(_unref($t)("Or")), 3)
          ])
        ])
      ]),
      rightItems: _withCtx(() => [
        _createElementVNode("div", _hoisted_3, [
          (_ctx.depth != 0)
            ? (_openBlock(), _createBlock(_component_b_btn, {
                key: 0,
                onClick: _cache[1] || (_cache[1] = ($event: any) => (emits('deleteGroup'))),
                class: "btn-icon mono delete mr-1",
                "data-cy": "group-clear"
              }, {
                default: _withCtx(() => [
                  _createVNode(Icon, { icon: "Close" })
                ]),
                _: 1
              }))
            : _createCommentVNode("", true),
          (_ctx.depth === 0 && 'isCaseSensitive' in vm.localModel)
            ? (_openBlock(), _createBlock(_component_b_form_checkbox, {
                key: 1,
                modelValue: vm.localModel.isCaseSensitive,
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((vm.localModel.isCaseSensitive) = $event)),
                class: "mr-3",
                "data-cy": "root-case-sensitive"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_unref($t)("caseSensitive")), 1)
                ]),
                _: 1
              }, 8, ["modelValue"]))
            : _createCommentVNode("", true)
        ])
      ]),
      _: 1
    }, 512),
    _createElementVNode("div", _hoisted_4, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(rules.value, (rule, index) => {
        return (_openBlock(), _createElementBlock("div", {
          key: `ruleId-${rule.ruleId}`,
          class: "rule-wrapper",
          ref_for: true,
          ref_key: "ruleWrapper",
          ref: ruleWrapper,
          id: `rule-${getCurrentPath()}-${_ctx.depth}-${index}`
        }, [
          _createVNode(Rule, {
            modelValue: rules.value[index],
            "onUpdate:modelValue": ($event: any) => ((rules.value[index]) = $event),
            onDeleteRule: onDeleteRule,
            path: getCurrentPath(),
            onRefreshBackground: triggerRefresh
          }, null, 8, ["modelValue", "onUpdate:modelValue", "path"])
        ], 8, _hoisted_5))
      }), 128)),
      (!hasGroupRule.value)
        ? (_openBlock(), _createBlock(Alert, {
            key: 0,
            show: "",
            "with-icon": "",
            variant: "warning"
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_unref($t)("AdvancedSearch.emptyGroup")), 1)
            ]),
            _: 1
          }))
        : _createCommentVNode("", true),
      _createElementVNode("div", {
        class: "query-builder-rule-criteria-control",
        ref_key: "queryBuilderRuleCriteriaControl",
        ref: queryBuilderRuleCriteriaControl
      }, [
        _createVNode(MSelect, {
          "data-cy": "rule-conditions",
          class: "query-builder-rule-criteria-selection",
          modelValue: vm.selectedColumnForRule,
          "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((vm.selectedColumnForRule) = $event)),
          placeholder: _unref($t)('AddRulePlaceholder'),
          items: possibleSelection.value,
          textField: "localizedKey"
        }, null, 8, ["modelValue", "placeholder", "items"]),
        _createVNode(_component_b_btn, {
          class: "muted",
          "data-cy": "advance-search-add-rule",
          onClick: _cache[4] || (_cache[4] = ($event: any) => (addRule())),
          disabled: !vm.selectedColumnForRule
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_unref($t)("addCriteria")), 1)
          ]),
          _: 1
        }, 8, ["disabled"]),
        _createElementVNode("span", _hoisted_6, _toDisplayString(_unref($t)("or")), 1),
        (_ctx.depth >= _unref(meta).config.advancedSearchMaxDepthAllowed - 1)
          ? _withDirectives((_openBlock(), _createElementBlock("span", _hoisted_7, [
              _createVNode(_component_b_btn, {
                class: "muted",
                "data-cy": "advance-search-add-group",
                onClick: _cache[5] || (_cache[5] = ($event: any) => (addGroup())),
                disabled: _ctx.depth >= _unref(meta).config.advancedSearchMaxDepthAllowed - 1
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_unref($t)("addGroup")), 1)
                ]),
                _: 1
              }, 8, ["disabled"])
            ])), [
              [_directive_tooltip, _unref($t)('api.errors.AdvancedSearch.MaxGroupLevelReached')]
            ])
          : (_openBlock(), _createBlock(_component_b_btn, {
              key: 1,
              class: "muted",
              "data-cy": "advance-search-add-group",
              onClick: _cache[6] || (_cache[6] = ($event: any) => (addGroup()))
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_unref($t)("addGroup")), 1)
              ]),
              _: 1
            }))
      ], 512)
    ]),
    (groups.value.length)
      ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(groups.value, (group, index) => {
            return (_openBlock(), _createElementBlock("div", {
              key: `group-${group.groupId}`,
              class: "group-wrapper",
              ref_for: true,
              ref_key: "groupWrapper",
              ref: groupWrapper,
              id: `group-${getCurrentPath()}-${_ctx.depth}-${index}`
            }, [
              _createVNode(_component_group, {
                modelValue: groups.value[index],
                "onUpdate:modelValue": ($event: any) => ((groups.value[index]) = $event),
                depth: _ctx.depth + 1,
                onDeleteGroup: ($event: any) => (onDeleteGroup(group)),
                onRefreshBackground: triggerRefresh,
                path: getCurrentPath()
              }, null, 8, ["modelValue", "onUpdate:modelValue", "depth", "onDeleteGroup", "path"])
            ], 8, _hoisted_9))
          }), 128))
        ]))
      : _createCommentVNode("", true),
    _createVNode(background)
  ], 8, _hoisted_1))
}
}

})